"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CUSTOM_APP_PLATFORM_AUTHORING_GUIDE = exports.CUSTOM_APP_DESIGN_QUALITY_RULES = exports.CUSTOM_APP_DESIGN_BASELINE_TOKENS = exports.CUSTOM_APP_PUBLIC_THEME_DEFAULTS = exports.CUSTOM_APP_DESIGN_BASELINE_VERSION = void 0;
exports.renderCustomAppDesignTokenDeclarations = renderCustomAppDesignTokenDeclarations;
const brand_1 = require("@streamlain/brand");
const custom_app_base_tokens_1 = require("./custom-app-base-tokens");
const custom_app_contract_1 = require("./custom-app-contract");
/** Default visual language for AI-authored Custom Apps. */
exports.CUSTOM_APP_DESIGN_BASELINE_VERSION = 'streamlain-default-v1';
exports.CUSTOM_APP_PUBLIC_THEME_DEFAULTS = {
    '--color-brand': '#5090d3',
    '--color-brand-dark': '#3a7bbf',
    '--color-brand-light': '#a8d4ff',
    '--color-dark': '#0a0a0a',
    '--color-body': '#424149',
    '--color-muted': '#8696a7',
    '--color-border': '#e3e2e4',
    '--color-surface': '#f8f8fa',
    '--color-accent-green': '#6ee7b7',
    '--color-accent-red': '#fca5a5',
    '--font-display': '"Playfair Display", Georgia, serif',
    '--font-ui': '"Space Grotesk", system-ui, sans-serif',
    '--font-body': '"Outfit", system-ui, sans-serif',
    '--radius-card': '16px',
    '--radius-button': '10px',
    '--max-width-container': '1200px',
    '--max-width-prose': '680px',
    '--streamlain-primary-color': 'var(--color-brand)',
    '--streamlain-text-primary-color': 'var(--color-dark)',
    '--streamlain-text-secondary-color': 'var(--color-muted)',
    '--streamlain-text-disable-color': '#b0b0b0',
    '--streamlain-background-primary-color': '#ffffff',
    '--streamlain-background-secondary-color': 'var(--color-surface)',
    '--streamlain-hover-color': 'var(--color-surface)',
    '--streamlain-border-color': 'var(--color-border)',
    '--streamlain-icon-color': 'var(--color-muted)',
    '--streamlain-error-color': '#dc2626',
    '--streamlain-background-error-color': '#fef2f2',
    '--streamlain-success-color': '#16a34a',
};
exports.CUSTOM_APP_DESIGN_BASELINE_TOKENS = {
    ...exports.CUSTOM_APP_PUBLIC_THEME_DEFAULTS,
    ...custom_app_base_tokens_1.CUSTOM_APP_BASE_TOKEN_DEFAULTS,
};
exports.CUSTOM_APP_DESIGN_QUALITY_RULES = [
    `Use the ${brand_1.BRAND_DISPLAY_NAME} baseline tokens unless the user explicitly requests a different visual language.`,
    'Use semantic landmarks, labels, keyboard navigation, visible focus states, and aria-live for asynchronous status.',
    'Design mobile-first without horizontal overflow and add a responsive breakpoint for multi-column layouts.',
    'Keep interactive targets at least 44px high and provide loading, empty, error, and success states.',
    'Respect prefers-reduced-motion and never rely on color alone to communicate state.',
    'Build multi-view and multi-route Custom Apps as a single-page application by default: preserve the shared shell and loaded state, update browser history client-side, and avoid document reloads. Use multi-page navigation only when the user explicitly requests it.',
];
const CUSTOM_APP_PLATFORM_CAPABILITY_CATALOG = {
    'dataset.readWindow': {
        purpose: 'Read a bounded, declared window of rows from one data source.',
        useWhen: 'Rendering initial or paged database-backed UI.',
    },
    'dataset.subscribe': {
        purpose: 'Receive invalidations for declared data sources.',
        useWhen: 'Keeping an internal-live SPA current; refetch the affected dataset after invalidation.',
    },
    'database.row.insert': {
        purpose: 'Insert one row through one declared database action.',
        useWhen: 'The mutation is a simple single-row insert without cross-database domain logic.',
    },
    'agentAction.invoke': {
        purpose: 'Invoke one published Agent Action owned by the mounted App Block.',
        useWhen: 'A form, Copilot, and MCP need the same validated relation-aware, multi-database, update, or upsert writer.',
    },
    'automation.run': {
        purpose: 'Start one declared automation action.',
        useWhen: 'The App Block exposes an existing automation as a user action.',
    },
    'attachment.update': {
        purpose: 'Update one declared attachment field.',
        useWhen: 'A user adds, replaces, or removes file-backed row content.',
    },
    'document.export': {
        purpose: 'Export a declared document or element in an allowed format.',
        useWhen: 'The UI offers a reviewed download or attachment save-back flow.',
    },
};
exports.CUSTOM_APP_PLATFORM_AUTHORING_GUIDE = {
    bridge: 'window.StreamLainCustomApp',
    discovery: 'window.StreamLainCustomApp.describe()',
    reads: [
        'readWindow(sourceKey, options)',
        'fetchDatasets(sourceKeys, options)',
        'subscribeDataset(sourceKey, listener)',
        'subscribeDatasets(sourceKeys, listener)',
    ],
    actions: [
        'execute({ controlId, input })',
        "executeCapability('agentAction.invoke', { input: { actionId, arguments } }) for internal App Block-owned actions",
    ],
    capabilities: custom_app_contract_1.CustomAppPlatformCapabilityIdSchema.options,
    capabilityCatalog: CUSTOM_APP_PLATFORM_CAPABILITY_CATALOG,
    agentActions: {
        purpose: 'Use one App-Block-owned Agent Action as the shared writer for validated domain operations, especially relational or multi-database mutations.',
        lifecycle: [
            'Inspect or propose with agent_tool_contracts.',
            'Validate the contract and bindings with agent_tool_contracts dry_run.',
            'Create or update the draft with agent_tool_contract_manage, then publish the validated immutable version.',
            "Declare agentAction.invoke on the mounted internal App Block route and call executeCapability('agentAction.invoke', { input: { actionId, arguments } }).",
        ],
        surfaces: [
            'A visible in-app form maps domain fields and relation selectors to the action arguments.',
            'Copilot and external MCP agents invoke the same published action contract.',
        ],
        boundary: 'Agent Actions belong to one App Block, require the exact mounted app/app-block/action scope, and are not available to portal or public routes.',
    },
    databaseColumnTypes: {
        title: 'Primary human-readable row label; exactly one per database.',
        'rich-text': 'Prose or stable external identifiers only.',
        number: 'Numeric quantities that must sort, filter, or aggregate numerically.',
        date: 'Dates and timestamps.',
        checkbox: 'Boolean yes/no state.',
        select: 'Exactly one value from a declared controlled vocabulary.',
        'multi-select': 'Multiple values from a declared controlled vocabulary.',
        attachment: 'Files and media.',
        relation: 'Links between rows or databases, including parent-child relationships.',
    },
    rules: [
        'Discover declared controls and data sources through describe(); do not invent bridge methods.',
        'Use the declared sourceKey/controlId values and render loading, empty, error, and success feedback.',
        'Design from a named real-world user journey before selecting routes, data sources, controls, or implementation details.',
        'Users refer to visible labels and current UI context. Resolve those references to exactly one declared source, row, and control; when zero or multiple visible candidates match, ask with visible alternatives instead of guessing.',
        'Keep row IDs, database block IDs, control IDs, SQL, and persistence details out of user-facing copy and input fields.',
        'Treat the Edgeless Database row ID as hidden stable identity. rowInsert creates it; users never provide or maintain it as a visible schema column.',
        'Define portable data shape through declared columns and bindings. Never make fixed business rows, row order, or starter-row IDs part of an App Block schema.',
        'Choose every database column type from its business meaning, never from the easiest rendering path: title for the primary label; rich-text only for prose and stable external IDs; number for quantities; date for dates and timestamps; checkbox for booleans; select or multi-select for controlled vocabularies; attachment for files; relation for links between rows or databases.',
        'Declare the complete allowed option set for every select and multi-select column. Declare target database, inverse field, and one/many cardinality for every relation. Model parent-child records as relations, never as visible text ID columns.',
        'Before apply, compare every planned database field with its declared semantic type. A schema that collapses statuses, priorities, categories, dates, booleans, attachments, or relations into rich-text is invalid and must be repaired before generation.',
        'Never call private platform endpoints directly from Custom App JavaScript.',
        'Use database.row.insert only for a simple declared single-row insert. Use an App-Block-owned Agent Action for validated domain operations, relation-aware or multi-database writes, updates, and upserts.',
        "Build human mutation forms from the same Agent Action contract used by Copilot and MCP. Submit them through executeCapability('agentAction.invoke', { input: { actionId, arguments } }); never add a form-only writer or private endpoint.",
        'Before wiring an Agent Action into an App Block, inspect or propose it with agent_tool_contracts, validate contract and bindings with dry_run, then create or update and publish it with agent_tool_contract_manage.',
        'Default multi-view and multi-route apps to SPA navigation. Keep real href fallbacks for accessibility, but handle ordinary in-app navigation with client-side history and render from already loaded state. Only generate a multi-page app when the user explicitly asks for one.',
    ],
};
function renderCustomAppDesignTokenDeclarations() {
    return Object.entries(exports.CUSTOM_APP_DESIGN_BASELINE_TOKENS)
        .map(([name, value]) => `${name}:${value}`)
        .join(';');
}
